import {
  EXCEL_COLUMN_FIELD_ALIASES,
  EXCEL_OPERATION_FIELD,
  EXCEL_MODE_OPERATION_HEADERS,
  TAG_BIND_MODE,
  getModeLabel,
  getOperationHeaderForMode,
} from './constants'

/**
 * 将表头单元格文本映射为内部字段名（title / key / operationTags 等）。
 */
function resolveFieldFromHeader(headerCell) {
  const text = String(headerCell || '').trim()
  if (!text) {
    return text
  }
  const entries = Object.entries(EXCEL_COLUMN_FIELD_ALIASES)
  for (let i = 0; i < entries.length; i += 1) {
    const [field, aliases] = entries[i]
    if (aliases.includes(text)) {
      return field
    }
  }
  return text
}

/** 表头行 canonical 化，保留原始文案供 mode 校验 */
export function canonicalizeExcelHeader(header = []) {
  const canonical = header.map((cell) => resolveFieldFromHeader(cell))
  canonical._raw = header.map((cell) => String(cell || '').trim())
  return canonical
}

/** 数据行按 canonical 字段名展开，兼容中文/英文表头导出的 xlsx */
export function canonicalizeExcelRow(row = {}) {
  const normalized = {}
  Object.keys(row).forEach((headerKey) => {
    const field = resolveFieldFromHeader(headerKey)
    normalized[field] = row[headerKey]
  })
  return normalized
}

/**
 * 解析 tag 单元格：英文/中文逗号或换行分隔，每段 trim。
 */
function normalizeTagNames(tagCell) {
  if (tagCell === null || tagCell === undefined) {
    return []
  }
  const raw = String(tagCell).trim()
  if (!raw) {
    return []
  }
  return raw.split(/[,，\n\r]+/).map((item) => item.trim()).filter(Boolean)
}

function resolveOperationValue(canonical) {
  if (canonical[EXCEL_OPERATION_FIELD] != null && canonical[EXCEL_OPERATION_FIELD] !== '') {
    return canonical[EXCEL_OPERATION_FIELD]
  }
  if (canonical.tag != null && canonical.tag !== '') {
    return canonical.tag
  }
  return ''
}

function detectModeFromRawHeader(rawHeader) {
  if (!rawHeader) {
    return null
  }
  if (rawHeader.includes('覆盖')) {
    return TAG_BIND_MODE.REPLACE
  }
  if (rawHeader.includes('删除')) {
    return TAG_BIND_MODE.REMOVE
  }
  if (rawHeader.includes('追加')) {
    return TAG_BIND_MODE.APPEND
  }
  return null
}

/**
 * 从打标表原始行提取全部 key（含操作列为空的行），用于「直接上传」自证作用范围。
 */
export function extractAllKeysFromRows(rows) {
  const keys = new Set()
  rows.forEach((row) => {
    const canonical = canonicalizeExcelRow(row)
    const resourceKey = canonical.key != null ? String(canonical.key).trim() : ''
    if (resourceKey) {
      keys.add(resourceKey)
    }
  })
  return keys
}

/**
 * 前端预校验：操作列中的 Tag 名须存在于全库名录（与后端 selectByNameSet 对齐）。
 * 覆盖模式且操作列为空表示清空，不校验 Tag 名。
 */
export function validateTagNamesInRows(parsedRows, validNameSet, mode) {
  const errors = []
  if (!validNameSet || validNameSet.size === 0) {
    return errors
  }
  parsedRows.forEach((row) => {
    if (mode === TAG_BIND_MODE.REPLACE && !(row.tagNames || []).length) {
      return
    }
    ;(row.tagNames || []).forEach((name) => {
      if (!validNameSet.has(name)) {
        errors.push({
          lineNo: row.lineNo,
          resourceKey: row.resourceKey,
          reason: `Tag「${name}」不存在，请对照 Tag 名录或检查拼写`,
        })
      }
    })
  })
  return errors
}

/**
 * 解析上传的 Excel 行，并校验 key 是否属于已确认的作用范围。
 * 追加/移除模式下操作列为空则跳过该行；覆盖模式空列表示清空。
 * @param {{ fileDefinesScope?: boolean }} options fileDefinesScope 为 true 时不校验 allowedKeySet（直接上传）
 */
export function parseExcelRows(rows, allowedKeySet, mode, options = {}) {
  const fileDefinesScope = options.fileDefinesScope === true
  const parsedRows = []
  const errors = []
  const seenKeys = new Set()

  rows.forEach((row, index) => {
    const canonical = canonicalizeExcelRow(row)
    const lineNo = index + 2
    const resourceKey = canonical.key != null ? String(canonical.key).trim() : ''
    if (!resourceKey) {
      errors.push({ lineNo, resourceKey: '', reason: '缺少 key' })
      return
    }
    if (seenKeys.has(resourceKey)) {
      errors.push({ lineNo, resourceKey, reason: 'key 重复' })
      return
    }
    seenKeys.add(resourceKey)

    if (!fileDefinesScope && allowedKeySet && !allowedKeySet.has(resourceKey)) {
      errors.push({ lineNo, resourceKey, reason: 'key 不在本次作用范围内' })
      return
    }

    const tagNames = normalizeTagNames(resolveOperationValue(canonical))
    if (mode !== TAG_BIND_MODE.REPLACE && tagNames.length === 0) {
      return
    }

    parsedRows.push({
      lineNo,
      title: canonical.title != null ? String(canonical.title) : '',
      resourceType: canonical.resourceType != null ? String(canonical.resourceType) : '',
      resourceKey,
      tagNames,
    })
  })

  return { parsedRows, errors }
}

export function validateExcelHeader(header = [], mode) {
  if (!mode) {
    return '请先在界面选择更新模式'
  }
  const canonicalHeader = canonicalizeExcelHeader(header)
  const rawHeaders = canonicalHeader._raw || []

  const required = ['title', 'resourceType', 'key']
  const missing = required.filter((col) => !canonicalHeader.includes(col))
  if (missing.length) {
    return `打标表缺少列：${missing.join(', ')}`
  }

  const operationIndex = canonicalHeader.findIndex(
    (col) => col === EXCEL_OPERATION_FIELD || col === 'tag'
  )
  if (operationIndex < 0) {
    return `缺少操作列：${getOperationHeaderForMode(mode)}`
  }

  const rawOperationHeader = rawHeaders[operationIndex] || ''
  const v2Headers = Object.values(EXCEL_MODE_OPERATION_HEADERS)
  if (v2Headers.includes(rawOperationHeader)) {
    const expected = getOperationHeaderForMode(mode)
    if (rawOperationHeader !== expected) {
      const detected = detectModeFromRawHeader(rawOperationHeader)
      const detectedLabel = detected ? getModeLabel(detected) : '其它'
      return `当前为${getModeLabel(mode)}模式，表头为「${rawOperationHeader}」（${detectedLabel}），请重新导出或切换模式`
    }
  }

  return ''
}
